<!--
  reservation-card-modal.vue

  2026-03-11: Separated canViewFinancials from canViewDetails for hide_financials flag
  2026-03-02: Redesigned to flat layout matching new design - no expandable sections
  2026-03-02: Currency conversion via zone_rate/currency_to for all financial amounts
  2026-01-05: Added 'Owner' source check to Cancel Vacation button condition
-->
<template>
  <v-card class="overflow-y-auto">
    <div class="pa-6">
      <!-- Header -->
      <div class="d-flex align-center justify-space-between mb-1 pr-8">
        <span class="text-h5 font-weight-bold">
          {{ $t('Reservation Details') }}
        </span>
        <div class="d-flex align-center" style="gap: 8px">
          <v-btn
            v-if="
              reservation.reservation_guesty_id &&
              !isInvestor &&
              !isCleaner &&
              !isCleaningSupervisor
            "
            :to="`/dashboard/reservation/${reservation.reservation_guesty_id}`"
            target="_blank"
            icon
            small
            outlined
          >
            <v-icon small>mdi-open-in-new</v-icon>
          </v-btn>
          <reservation-create-modal
            v-if="!isInvestor && manuallyEditable"
            :key="reservation.id"
            :owner-vacation="reservation.source === 'Owner'"
            :is-edit-mode="true"
            :edit-reservation="reservation"
            @change="getListing"
          />
          <reservation-edit-modal
            v-else-if="isInvestor && reservation.source === 'Owner'"
            :key="reservation.id"
            :reservation="reservation"
            :listing="{ id: reservation.listing_id }"
            @change="getListing"
          />
          <span v-if="reservation.confirmation_code" class="confirmation-code">
            {{ reservation.confirmation_code }}
          </span>
          <v-btn icon small @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Booking info row -->
      <v-divider class="mt-4 mb-4" />
      <v-row no-gutters>
        <v-col cols="6" md="3">
          <div class="info-label">{{ $t('Check-in') }}</div>
          <div class="info-value">{{ checkInDatetime }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="info-label">{{ $t('Check-out') }}</div>
          <div class="info-value">{{ checkOutDatetime }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="info-label">{{ $t('Guests Count') }}</div>
          <div class="info-value">
            {{ reservation.guests_count || 'N/A' }}
          </div>
        </v-col>
        <v-col v-if="!shouldHideChannels" cols="6" md="3">
          <div class="info-label">{{ $t('Source') }}</div>
          <div class="info-value">
            {{ reservation.source || 'Direct' }}
          </div>
        </v-col>
      </v-row>

      <!-- Guest info row -->
      <template v-if="canViewDetails && reservation.guest">
        <v-divider class="mt-4 mb-4" />
        <v-row no-gutters>
          <v-col cols="4">
            <div class="info-label">{{ $t('Guest Name') }}</div>
            <div class="info-value text-capitalize">
              {{ displayGuestName(guestName) }}
            </div>
          </v-col>
          <v-col v-if="(isSmartBnb || isAdmin) && !isInvestor" cols="4">
            <div class="info-label">{{ $t('Guest Email') }}</div>
            <div class="info-value">{{ guestEmail }}</div>
          </v-col>
          <v-col v-if="(isSmartBnb || isAdmin) && !isInvestor" cols="4">
            <div class="info-label">{{ $t('Phone') }}</div>
            <div class="info-value">{{ guestPhone }}</div>
          </v-col>
        </v-row>
      </template>

      <!-- Financial Information -->
      <template v-if="canViewFinancials">
        <v-divider class="mt-5 mb-5" style="border-style: dashed" />
        <div class="text-h6 font-weight-bold mb-4">
          {{ $t('Financial Information') }}
        </div>

        <!-- Summary cards -->
        <v-row class="mb-5" dense>
          <v-col v-if="!isInvestor" cols="12" md="6">
            <div class="summary-card summary-card--green">
              <div class="summary-card__label summary-card__label--green">
                {{ $t('Host Payout') }}
              </div>
              <div class="summary-card__value summary-card__value--green">
                {{ formatCurrency(reservation.host_payout) }}
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="summary-card summary-card--green">
              <div class="summary-card__label summary-card__label--green">
                {{ $t('Net Income') }}
              </div>
              <div class="summary-card__value summary-card__value--green">
                {{ formatCurrency(incomeValue) }}
              </div>
            </div>
          </v-col>
          <v-col v-if="isInvestor" cols="12" md="6">
            <div class="summary-card summary-card--orange">
              <div class="summary-card__label summary-card__label--orange">
                {{ $t('Management Commission') }}
              </div>
              <div class="summary-card__value summary-card__value--orange">
                {{ formatCurrency(reservation.management_commission) }}
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Breakdown rows -->
        <div v-if="showFinanceBreakdown">
          <div v-for="(row, i) in breakdownRows" :key="i" class="breakdown-row">
            <div
              v-for="(item, j) in row"
              :key="j"
              class="breakdown-cell"
              :class="
                j === 0 ? 'breakdown-cell--left' : 'breakdown-cell--right'
              "
            >
              <template v-if="item">
                <span class="breakdown-cell__label">{{ item.label }}</span>
                <span
                  class="breakdown-cell__value"
                  :style="item.color ? { color: item.color } : {}"
                >
                  {{ item.value }}
                </span>
              </template>
            </div>
          </div>
        </div>
      </template>

      <!-- Notes -->
      <template v-if="reservation.dvr_notes && canViewDetails && !isInvestor">
        <v-divider class="mt-4 mb-4" />
        <div class="info-label">{{ $t('Notes') }}</div>
        <p class="text-body-2 mt-1" style="color: #111827">
          {{ reservation.dvr_notes }}
        </p>
      </template>

      <!-- Cleaning Task link -->
      <template v-if="reservation.cleaning_listing_task_id && !isInvestor">
        <v-divider class="mt-4 mb-4" />
        <div class="info-label">{{ $t('Cleaning Task') }}</div>
        <router-link
          :to="{
            name: 'lt-page',
            params: { id: reservation.cleaning_listing_task_id },
          }"
          target="_blank"
          class="text-body-2 mt-1 d-inline-block"
          style="color: #2563eb"
        >
          #{{ reservation.cleaning_listing_task_id }}
        </router-link>
      </template>

      <!-- Cancel Vacation -->
      <div
        v-if="
          (reservation.source === 'Vacation' ||
            reservation.source === 'Owner') &&
          (isAdmin || (isInvestor && ownerVacationAllowed))
        "
        class="d-flex justify-end mt-5"
      >
        <v-btn color="warning" @click="cancelVaction">
          {{ $t('Cancel Vacation') }}
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script>
import CommonMixin from 'components/mixins/common_functions'
import FormattersMixin from 'components/mixins/formatters-mixin'
import PermissionsMixin from 'components/mixins/permissions-mixin'
import axios from 'axios'
import ReservationEditModal from 'components/reservation/reservation-edit-modal.vue'
import ReservationCreateModal from 'components/reservation/reservation-create-modal.vue'

const INCOME_FIELD_MAP = {
  client_price: 'client_price',
  fare_accommodation: 'fare_accommodation',
  net_income: 'net_income',
  price_total: 'price',
}

export default {
  name: 'ReservationCardModal',
  components: {
    ReservationCreateModal,
    ReservationEditModal,
  },
  mixins: [FormattersMixin, PermissionsMixin, CommonMixin],
  props: ['reservation', 'timezone'],
  computed: {
    appSettings() {
      return this.$store.state.app.configuration || {}
    },
    ownerVacationAllowed() {
      const listingId = this.reservation?.listing_id
      const all =
        this.$store.state.listings?.listings ||
        this.$store.state.listingsNoScope ||
        []
      const match = listingId ? all.find(l => l.id === listingId) : null
      const listing = match || this.$store.state.currentListing
      return !!listing?.owner_vacation_allowed
    },
    canViewDetails() {
      return (
        !this.isCleaner &&
        !this.isCleaningManager &&
        !this.isCleaningSupervisor &&
        !this.isFieldPm
      )
    },
    canViewFinancials() {
      // Backend sets `hide_financials: true` on unpaid-reservation rows when
      // the hide-unpaid-reservations setting is on for this view. Suppress the
      // whole financial section in that case — money fields are nulled and
      // showing them (as 0 / fallback values) would be misleading.
      return (
        this.canViewDetails &&
        !this.hideFinancials &&
        !this.reservation.hide_financials
      )
    },
    hasZoneConversion() {
      return (
        this.reservation.currency_to &&
        this.reservation.zone_rate &&
        this.reservation.zone_rate !== 1
      )
    },
    showFinanceBreakdown() {
      if (!this.isInvestor) return true
      return !this.appSettings?.dashboard_settings?.hide_reservation_breakdown
    },
    shouldHideChannels() {
      return (
        this.isInvestor &&
        this.$store.state.app.configuration?.ui_settings?.hide_income_channels
      )
    },
    ownerIncomeFormula() {
      // "Hide reservation breakdown" promises investors will only see the total
      // price (gross amount paid by guest), regardless of any net/profit formula
      // configured on the listing or business model.
      if (
        this.isInvestor &&
        this.appSettings?.dashboard_settings?.hide_reservation_breakdown
      ) {
        return 'price_total'
      }
      const listing = this.$store.state.currentListing || {}
      return (
        listing.business_model?.owners_income_formula ||
        listing.owners_income_formula ||
        (this.appSettings && this.appSettings.owners_income_formula) ||
        'fare_accommodation'
      )
    },
    incomeValue() {
      return (
        this.reservation[INCOME_FIELD_MAP[this.ownerIncomeFormula]] ||
        this.reservation.fare_accommodation
      )
    },
    manuallyEditable() {
      return (
        this.reservation.reservation_type === 'long_term' ||
        (this.reservation.app_integration_name === 'boom' &&
          this.reservation.source === 'manual') ||
        this.reservation.source === 'Simple Booking'
      )
    },
    guestName() {
      if (this.reservation.source === 'Owner') {
        return 'Owner Vacation'
      }
      return (
        this.reservation.guest_name ||
        this.reservation.guest?.full_name ||
        `${this.reservation.first_name || ''} ${
          this.reservation.last_name || ''
        }`.trim() ||
        'Guest'
      )
    },
    guestEmail() {
      return (
        this.reservation.primary_email ||
        this.reservation.guest?.emails?.[0] ||
        this.reservation.email ||
        'N/A'
      )
    },
    guestPhone() {
      return (
        this.reservation.primary_phone ||
        this.reservation.guest?.phones?.[0] ||
        this.reservation.phone ||
        'N/A'
      )
    },
    checkInDatetime() {
      const time = this.reservation.planned_arrival
        ? this.reservation.planned_arrival
        : this.parseDateTZ(this.reservation.check_in, 'HH:mm', this.timezone)
      return `${this.parseDateTZ(
        this.reservation.check_in,
        'YYYY-MM-DD',
        this.timezone
      )} ${time}`
    },
    checkOutDatetime() {
      const time = this.reservation.planned_departure
        ? this.reservation.planned_departure
        : this.parseDateTZ(this.reservation.check_out, 'HH:mm', this.timezone)
      return `${this.parseDateTZ(
        this.reservation.check_out,
        'YYYY-MM-DD',
        this.timezone
      )} ${time}`
    },
    breakdownRows() {
      const items = []

      if (this.reservation.fare_accommodation && !this.isInvestor) {
        items.push({
          label: this.$t('Accommodation'),
          value: this.formatCurrency(this.reservation.fare_accommodation),
        })
      }
      if ((this.isSmartBnb || this.isAdmin) && this.reservation.cleaning_fee) {
        items.push({
          label: this.$t('Cleaning'),
          value: this.formatCurrency(this.reservation.cleaning_fee),
        })
      }
      if (this.reservation.total_taxes && !this.isInvestor) {
        items.push({
          label: this.$t('Taxes'),
          value: this.formatCurrency(this.reservation.total_taxes),
        })
      }
      if (this.reservation.host_channel_fee && !this.isInvestor) {
        items.push({
          label: this.$t('Channel Fee'),
          value: this.formatCurrency(this.reservation.host_channel_fee),
        })
      }
      if (this.reservation.processing_fee && !this.isInvestor) {
        items.push({
          label: this.$t('Processing Fee'),
          value: this.formatCurrency(this.reservation.processing_fee),
        })
      }
      if (this.reservation.management_commission && !this.isInvestor) {
        items.push({
          label: this.$t('Management Fee'),
          value: this.formatCurrency(this.reservation.management_commission),
          color: '#ea580c',
        })
      }

      // Pair items into rows of 2
      const rows = []
      for (let i = 0; i < items.length; i += 2) {
        rows.push([items[i], items[i + 1] || null])
      }
      return rows
    },
  },
  methods: {
    formatCurrency(amount) {
      if (this.hasZoneConversion) {
        return this.toMoney(amount ? amount * this.reservation.zone_rate : 0, {
          currency: this.reservation.currency_to,
        })
      }
      return this.toMoney(amount, {
        currency: this.reservation.currency,
      })
    },
    getListing() {
      this.$store.dispatch('changeListing', {
        listingId: this.reservation.listing_id,
      })
    },
    cancelVaction() {
      const userConfirm = confirm(
        this.$t('Are you sure you want to cancel this vacation?')
      )
      if (userConfirm) {
        axios
          .delete(`/api/owner-vacation/${this.reservation.id}`)
          .then(() => {
            this.$store.dispatch('changeListing', {
              listingId: this.$store.state.currentListing.id,
            })
            this.dialog = false
          })
          .catch(error => {
            alert(error)
          })
      }
    },
    close() {
      this.$store.commit('hideModal')
    },
  },
}
</script>

<style scoped>
.confirmation-code {
  font-family: monospace;
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 4px 10px;
}

.info-label {
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.info-value {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-top: 2px;
}

.summary-card {
  border-radius: 8px;
  padding: 16px;
}

.summary-card--green {
  background-color: #f0fdf4;
}

.summary-card--orange {
  background-color: #fff7ed;
}

.summary-card__label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.summary-card__label--green {
  color: #15803d;
}

.summary-card__label--orange {
  color: #c2410c;
}

.summary-card__value {
  font-size: 24px;
  font-weight: 700;
  margin-top: 4px;
}

.summary-card__value--green {
  color: #15803d;
}

.summary-card__value--orange {
  color: #c2410c;
}

.breakdown-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-bottom: 1px dashed #e5e7eb;
}

.breakdown-row:last-child {
  border-bottom: none;
}

.breakdown-cell {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
}

.breakdown-cell--left {
  padding-right: 16px;
}

.breakdown-cell--right {
  padding-left: 16px;
}

.breakdown-cell__label {
  font-size: 14px;
  color: #4b5563;
}

.breakdown-cell__value {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}
</style>
